import { useEffect, useState } from 'react'
import { RefreshCw, CheckCircle, XCircle, Loader2, FileText, Filter } from 'lucide-react'
import { leavesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

const getApplicant = (l) => l.applicant || l.userId || l.user || null
const getApplicantName = (l) => {
  const a = getApplicant(l)
  if (!a) return l.name || 'Unknown'
  return a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Unknown'
}
const getApplicantRole = (l) => {
  const a = getApplicant(l)
  return a?.role || l.role || ''
}
const getApplicantClass = (l) => {
  const c = l.classId || l.class
  return c?.name || ''
}

const statusColors = {
  Pending: 'bg-yellow-50 text-yellow-700',
  Approved: 'bg-green-50 text-green-700',
  Rejected: 'bg-red-50 text-red-600',
}

export default function LeavesPage() {
  const [leaves, setLeaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('Pending')
  const [reviewing, setReviewing] = useState({})

  const fetchLeaves = async () => {
    try {
      setLoading(true)
      const data = await leavesApi.getAll(filter !== 'All' ? { status: filter } : {})
      setLeaves(Array.isArray(data) ? data : data?.leaves ?? [])
    } catch { toast.error('Failed to load leaves'); setLeaves([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchLeaves() }, [filter])

  const handleReview = async (id, status) => {
    try {
      setReviewing((p) => ({ ...p, [id]: true }))
      await leavesApi.review(id, { status })
      setLeaves((prev) => prev.map((l) => (l._id || l.id) === id ? { ...l, status } : l))
      toast.success(`Leave ${status.toLowerCase()}`)
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to review') }
    finally { setReviewing((p) => ({ ...p, [id]: false })) }
  }

  const filtered = filter === 'All' ? leaves : leaves.filter((l) => l.status === filter)

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Leave Requests</h2>
          <p className="text-sm text-gray-500 mt-0.5">{loading ? '...' : `${filtered.length} requests`}</p>
        </div>
        <button onClick={fetchLeaves} className="btn-secondary p-2 self-start sm:self-auto"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* Filter tabs */}
      <div className="card p-3 flex gap-2 flex-wrap">
        {['All', 'Pending', 'Approved', 'Rejected'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${filter === s ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >{s}</button>
        ))}
      </div>

      {/* Leaves list */}
      <div className="space-y-3">
        {loading ? [...Array(4)].map((_, i) => (
          <div key={i} className="card p-4 animate-pulse flex items-start gap-4">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-100 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          </div>
        )) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FileText className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm">No {filter !== 'All' ? filter.toLowerCase() : ''} leave requests.</p>
          </div>
        ) : filtered.map((l) => {
          const lid = l._id || l.id
          const isReviewing = reviewing[lid]
          return (
            <div key={lid} className="card p-4">
              <div className="flex items-start gap-4">
                <Avatar src={getApplicant(l)?.avatar} name={getApplicantName(l)} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{getApplicantName(l)}</p>
                      <p className="text-xs text-gray-400">{getApplicantRole(l)} {getApplicantClass(l) ? `· ${getApplicantClass(l)}` : ''}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${statusColors[l.status] || 'bg-gray-100 text-gray-600'}`}>{l.status}</span>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    <p className="font-medium">{l.leaveType || l.type || 'Leave'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {l.fromDate ? new Date(l.fromDate).toLocaleDateString('en-IN') : ''} {l.toDate ? `→ ${new Date(l.toDate).toLocaleDateString('en-IN')}` : ''}
                      {l.days ? ` (${l.days} day${l.days !== 1 ? 's' : ''})` : ''}
                    </p>
                  </div>
                  {l.reason && <p className="text-xs text-gray-500 mt-2 bg-gray-50 rounded-lg px-3 py-2 italic">"{l.reason}"</p>}

                  {l.status === 'Pending' && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleReview(lid, 'Approved')}
                        disabled={isReviewing}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors disabled:opacity-50"
                      >
                        {isReviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        Approve
                      </button>
                      <button
                        onClick={() => handleReview(lid, 'Rejected')}
                        disabled={isReviewing}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        {isReviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
